import React from 'react';

import { SambaAutoLink } from '@betterment/shared-components';

import RateBoostPercentImage from '../images/rate_boost_percent.png';
import RewardGiftBoxImage from '../images/reward_gift_box.png';
import RewardGiftBoxMobileImage from '../images/reward_gift_box_wide.png';
import RateBoostPercent75Image from '../images/rewards_center_images/cash_rate_boost.png';
import { urlBuilder } from '../Shared/urlBuilder';

export type SenderOfferName =
  | 'referral_sender_q4_2018'
  | 'referral_sender_dmf_q4_2019'
  | 'referral_sender_rate_boost_q1_2024'
  | 'referral_sender_rate_boost_q4_2025';

export type OfferNameMap = {
  [K in SenderOfferName]: string;
};

export interface ReferralSenderOfferName {
  senderOfferName: SenderOfferName | null;
}

export type Status = 'funded' | 'signed_up' | 'sent' | 'prompt';

export type StatusMap = {
  [K in Status]: string;
};

export const displayStatusMessages: StatusMap = {
  funded: 'Funded',
  signed_up: 'Signed Up',
  sent: 'Invite sent',
  prompt: 'Resend invite',
};

export interface ReferralSenderOffer {
  offerName: string;
  senderReward: string;
  receiverReward: string;
  offerTermsUrl: string;
}

export interface ReferralKey {
  referralKey: string;
}

export interface InviteParams {
  receivingUserEmail: string;
}

export interface Referral {
  email: string;
  status: Status;
  personalReferralInviteId: string | null;
}

export type Referrals = Referral[] | [];

export interface TreeParams {
  name: string;
  referrals: Referral[] | null;
  handleModalClick: () => void;
  isLoading: boolean;
  numberOfSuccessfulReferrals: number;
}

export interface LeafProps {
  email: string;
  status: Status;
  personalReferralInviteId: string | null;
  isExtended: boolean;
  handleModalClick: () => void;
}

export interface FriendProps {
  num?: number;
  message?: string;
  isExtended: boolean;
  isLoading: boolean;
  handleModalClick: () => void;
}

export type NodeProps = {
  isExtended: boolean;
  width: number[];
};

export type SenderOfferFulfillment = {
  status: 'Upcoming' | 'Active' | 'Expired' | 'Pending';
  displayReward: string;
  rewardType: 'free_dollars_fixed' | 'free_time' | 'savings_rate_boost';
  startDate: string;
  endDate: string;
  offerName: SenderOfferName;
};

export type ReferralSenderOfferDetails = {
  name: SenderOfferName;
  title: string;
  description: string;
  additionalDescription?: React.ReactNode;
  limitedTime: boolean;
  featuredImage: {
    image: string;
    mobile: string;
    alt: string;
  };
  titleV2?: string;
};

export const REFERRAL_SENDER_OFFER_LOOKUP: Record<SenderOfferName, ReferralSenderOfferDetails> = {
  referral_sender_q4_2018: {
    name: 'referral_sender_q4_2018',
    title: 'Get your investing account balance (excluding crypto accounts) managed free for one month or more',
    description:
      'Give your friends the gift of better investing and get your investing account balance (excluding crypto accounts) managed free for one month or more.',
    limitedTime: false,
    featuredImage: {
      image: RewardGiftBoxImage,
      mobile: RewardGiftBoxMobileImage,
      alt: 'hand with gift box',
    },
  },
  referral_sender_dmf_q4_2019: {
    name: 'referral_sender_dmf_q4_2019',
    title: 'Get up to $25,000 of your investments managed for free',
    description:
      "Refer a friend to sign up for Betterment, and we'll manage $5,000 of your investment balance free for a year. Limit five referrals for a total of $25,000 investment dollars managed.",
    limitedTime: false,
    featuredImage: {
      image: RewardGiftBoxImage,
      mobile: RewardGiftBoxMobileImage,
      alt: 'hand with gift box',
    },
  },
  referral_sender_rate_boost_q1_2024: {
    name: 'referral_sender_rate_boost_q1_2024',
    title: 'Get up to 4.50% variable APY for 3 months',
    titleV2: 'Get up to 4.25% variable APY for 3 months',
    description:
      "Refer a friend to Betterment and you'll both receive a 0.50% APY boost on Cash Reserve. You'll get the boost when your friend signs up and funds a qualifying account within 7 days. Limit one boost per customer.",
    limitedTime: true,
    featuredImage: {
      image: RateBoostPercentImage,
      mobile: RateBoostPercentImage,
      alt: '+0.50% APY',
    },
  },
  referral_sender_rate_boost_q4_2025: {
    name: 'referral_sender_rate_boost_q4_2025',
    title: 'Get up to 4.25% variable APY for 3 months',
    titleV2: 'Get up to 4.25% variable APY for 3 months',
    description:
      "Refer a friend to Betterment and you'll both receive a 0.75% APY boost on Cash Reserve. You'll get the boost when your friend signs up and funds a qualifying account within 7 days. Limit one boost per customer.",
    additionalDescription: (
      <>
        Boost applies to balances up to $150,000.{' '}
        <SambaAutoLink to={urlBuilder.getOfferTerms.absolute({ offerName: 'referral_sender_rate_boost_q4_2025' })}>
          Terms Apply.
        </SambaAutoLink>
      </>
    ),
    limitedTime: true,
    featuredImage: {
      image: RateBoostPercent75Image,
      mobile: RateBoostPercent75Image,
      alt: '+0.75% APY',
    },
  },
};
